import { useCallback, useMemo } from 'react';
import { useFilter } from './useFilter';
import { useSelector } from 'react-redux';
import { useUserProfileContext } from '../context/UserProfileContext';
import { useDefaultProfile } from './useDefaultProfile';
import { getSelectedAuction } from '../store/selectors/auctionSelectors';

const url = window.location.href;
const path = window.location.pathname;

const generateSessionId = () => {
  const sessionId = `session_${Date.now()}_${Math.random()
    .toString(36)
    .substr(2, 9)}`;
  sessionStorage.setItem('sessionId', sessionId);
  return sessionId;
};

export const useSegment = (isRecommended = false, isRecommendedByDetails = false) => {
  const { filterList } = useFilter();
  const applyedFilter = useSelector(state => state.ui.filters.newOffersFiltersApplyed);
  const { profile } = useSelector(state => state.user.profile || {});
  const { user } = useSelector(state => state.authentication);
  const { currentUserProfile } = useUserProfileContext();
  const defaultProfile = useDefaultProfile();
  const selectedAuction = useSelector(getSelectedAuction);

  const setUser = {
    userId: user?.id,
    dealershipName: user?.dealershipName,
    email: user?.email,
    phoneNumber: currentUserProfile?.phoneNumber,
    relatedNinja: profile?.relatedNinja,
    defaultProfile: defaultProfile,
  };

  const formatSections = useMemo(() => {
    if (applyedFilter) return 'conventionalFilter';
    else if (!!filterList?.length) return 'categories';
    else if (isRecommendedByDetails) return 'recomendados';
    else if (isRecommended) return 'orderCars';
    else return 'newOffers';
  }, [applyedFilter, filterList, isRecommended, isRecommendedByDetails]);

  const applyDefaults = useCallback(
    props => {
      props.user = setUser;

      if (!props.section) {
        props.section = formatSections;
      }

      if (props.hotTarget === undefined) {
        props.hotTarget = selectedAuction?.summary?.vehiclePricing?.hotTarget || null;
        console.log('⚠️ hotTarget não foi passado, pegando do Redux:', props.hotTarget);
      } else {
        console.log('✅ hotTarget foi passado pelo componente:', props.hotTarget);
      }

      if (!props.sessionId) {
        props.sessionId = sessionStorage.getItem('sessionId') || generateSessionId();
      }

      if (!props.date_hour) {
        props.date_hour = new Date().toISOString();
      }

      return props;
    },
    [setUser, formatSections, selectedAuction]
  );

  const validateMonetaryData = useCallback((props, bidFields = ['bid_amount']) => {
    const validationErrors = [];

    bidFields.forEach(field => {
      const value = props[field];
      if (value !== null && value !== undefined) {
        if (typeof value !== 'number' || value <= 0) {
          validationErrors.push(`${field} inválido: ${value}`);
          console.error(`Validação falhou - ${field} deve ser > 0:`, value);
        }
      }
    });

    if (props.hotTarget !== null && props.hotTarget !== undefined) {
      if (typeof props.hotTarget !== 'number' || props.hotTarget < 0) {
        validationErrors.push(`hotTarget inválido: ${props.hotTarget}`);
        console.error('Validação falhou - hotTarget deve ser >= 0:', props.hotTarget);
      }
    }

    return validationErrors;
  }, []);

  const calculatePriceData = useCallback(
    (props, primaryBidField = 'bid_amount', validationErrors = []) => {
      const bidAmount = props[primaryBidField] || props.bid_amount || props.bidAmount;

      if (
        props.hotTarget !== null &&
        props.hotTarget !== undefined &&
        bidAmount !== null &&
        bidAmount !== undefined &&
        validationErrors.length === 0
      ) {
        const hotValue = parseFloat(parseFloat(props.hotTarget).toFixed(2));
        const bidAmountValue = parseFloat(parseFloat(bidAmount).toFixed(2));
        
        return {
          hot_value: hotValue,
          bid_amount: bidAmountValue,
          temperature_ratio: hotValue > 0 
            ? parseFloat((bidAmountValue / hotValue).toFixed(4))
            : null,
        };
      } else {
        if (props.hotTarget === null || props.hotTarget === undefined) {
          console.warn(`Valor quente (hotTarget) ausente:`, {
            inspection_id: props.inspection_id,
            auction_id: props.auction_id,
            [primaryBidField]: bidAmount,
          });
        }

        return {
          hot_value:
            props.hotTarget !== null ? parseFloat(parseFloat(props.hotTarget).toFixed(2)) : null,
          bid_amount: bidAmount !== null ? parseFloat(parseFloat(bidAmount).toFixed(2)) : null,
          temperature_ratio: null,
        };
      }
    },
    []
  );

  const logAuditoria = useCallback(
    (eventName, props, validationErrors, extraFields = {}) => {
      if (validationErrors.length > 0) {
        console.error(`ERRO DE VALIDAÇÃO - ${eventName}:`, {
          errors: validationErrors,
          props_received: props,
          timestamp: new Date().toISOString(),
          user_id: setUser.userId,
        });
      }

      console.log(`📊 ${eventName} - AUDITORIA:`, {
        timestamp: props.date_hour,
        user_id: setUser.userId,
        session_id: props.sessionId,
        inspection_id: props.inspection_id,
        auction_id: props.auction_id,
        hot_value: props.hot_value,
        bid_amount: props.bid_amount,
        temperature_ratio: props.temperature_ratio,
        section: props.section,
        validation_errors: validationErrors,
        ...extraFields,
      });
    },
    [setUser]
  );

  const processEvent = useCallback(
    (eventName, props, options = {}) => {
      const {
        primaryBidField = 'bid_amount',
        extraValidationFields = [],
        extraAuditFields = {},
        cleanupFields = ['hotTarget'],
      } = options;

      props = applyDefaults(props);
      const validationErrors = validateMonetaryData(props, [
        primaryBidField,
        ...extraValidationFields,
      ]);

      const priceData = calculatePriceData(props, primaryBidField, validationErrors);
      Object.assign(props, priceData);

      [...extraValidationFields, 'new_bid_value', 'last_bid_value', 'bidAmount'].forEach(field => {
        if (props[field] && typeof props[field] === 'number') {
          props[field] = parseFloat(parseFloat(props[field]).toFixed(2));
        }
      });

      logAuditoria(eventName, props, validationErrors, extraAuditFields);

      cleanupFields.forEach(field => delete props[field]);

      analytics.track(eventName, props);
    },
    [applyDefaults, validateMonetaryData, calculatePriceData, logAuditoria]
  );

  const parseArguments = useCallback(
    (args, fieldMap) => {
      if (typeof args[0] === 'object' && args[0] !== null) {
        return args[0];
      }
      const props = {};
      fieldMap.forEach((field, index) => {
        if (args[index] !== undefined) {
          props[field] = args[index];
        }
      });
      if (!props.section) {
        props.section = formatSections;
      }

      return props;
    },
    [formatSections]
  );

  const page = useCallback(
    title => {
      analytics.page(title, {
        user: setUser,
        title,
        url,
        path,
      });
    },
    [setUser]
  );

  const trackViewedDetails = useCallback(
    (auction, section = formatSections) => {
      const toTrack = {
        user: setUser,
        inspection_id: auction?._id,
        auction_id: auction?.auction?.idAuction,
        section,
        make: auction?.summary?.make,
        model: auction?.summary?.model,
        year: auction?.summary?.year,
        viewCount: auction?.auction?.viewCount,
        dealerViewCount:
          auction?.auction?.viewUsers?.find(user => user?._id === setUser?.userId)?.viewCount || 0,
        participating: auction?.auction?.bids?.map(bid => bid?.dealer?.email),
      };
      analytics.track('Details Viewed', toTrack);
    },
    [formatSections, setUser]
  );

  const trackAuctionViewedDetails = useCallback(
    (status, auction_id) => {
      analytics.track('Auction DetailsViewed', {
        user: setUser,
        status,
        auction_id,
        platform: 'web',
      });
    },
    [setUser]
  );

  const trackAuctionRecommendedViewedDetails = useCallback(
    (
      original_auction_id,
      selected_auction_id,
      original_status,
      selected_status,
      position,
      total_items
    ) => {
      analytics.track('Recommended Auction Clicked ', {
        user: setUser,
        original_auction_id,
        selected_auction_id,
        original_status,
        selected_status,
        position,
        total_items,
        platform: 'web',
      });
    },
    [setUser]
  );

  const trackButtonClicked = useCallback(
    (...args) => {
      const props = parseArguments(args, [
        'inspection_id',
        'type',
        'auction_id',
        'section',
        'date_hour',
        'hotTarget',
        'bid_amount',
        'click_position',
      ]);

      processEvent('Bid Clicked', props, {
        extraAuditFields: { type: props.type },
      });
    },
    [parseArguments, processEvent]
  );

  const trackConfirmBid = useCallback(
    props => {
      // Normalizar bidAmount para bid_amount
      if (props.bidAmount && !props.bid_amount) {
        props.bid_amount = props.bidAmount;
      }

      processEvent('Bid Confirmed', props, {
        cleanupFields: ['hotTarget', 'bidAmount'],
      });
    },
    [processEvent]
  );

  const trackRemoveAutoBid = useCallback(
    (...args) => {
      const props = parseArguments(args, ['inspection_id', 'autobid_id', 'auction_id', 'section']);

      processEvent('Automatic Offer Removed', props, {
        extraAuditFields: { autobid_id: props.autobid_id },
      });
    },
    [parseArguments, processEvent]
  );

  const trackChangeMaximumAutoBid = useCallback(
    (...args) => {
      const props = parseArguments(args, [
        'inspection_id',
        'autobid_id',
        'new_bid_value',
        'last_bid_value',
        'section',
      ]);

      if (props.new_bid_value && !props.bid_amount) {
        props.bid_amount = props.new_bid_value;
      }

      processEvent('Automatic Offer Maximum Bid Changed', props, {
        primaryBidField: 'new_bid_value',
        extraValidationFields: ['last_bid_value'],
        extraAuditFields: {
          autobid_id: props.autobid_id,
          new_bid_value: props.new_bid_value,
          last_bid_value: props.last_bid_value,
        },
      });
    },
    [parseArguments, processEvent]
  );

  const trackTieAutoBid = useCallback(
    (...args) => {
      const props = parseArguments(args, ['inspection_id', 'autobid_id', 'bidAmount', 'section']);

      // Normalizar bidAmount para bid_amount
      if (props.bidAmount && !props.bid_amount) {
        props.bid_amount = props.bidAmount;
      }

      processEvent('Automatic Offer Tied', props, {
        primaryBidField: 'bidAmount',
        extraAuditFields: {
          autobid_id: props.autobid_id,
          bidAmount: props.bidAmount,
        },
        cleanupFields: ['hotTarget'], // Manter bidAmount original
      });
    },
    [parseArguments, processEvent]
  );

  const trackReachedLimitAutoBid = useCallback(
    (...args) => {
      const props = parseArguments(args, ['inspection_id', 'autobid_id', 'auction_id', 'section']);

      processEvent('Automatic Offer Limit Reached', props, {
        extraAuditFields: { autobid_id: props.autobid_id },
      });
    },
    [parseArguments, processEvent]
  );

  const trackCarouselInteraction = useCallback(
    (inspection_id, auction_id, section = formatSections) => {
      analytics.track('Carousel Interaction', {
        user: setUser,
        inspection_id,
        auction_id,
        section,
      });
    },
    [formatSections, setUser]
  );

  const trackFilter = useCallback(
    (type, criteria, results) => {
      analytics.track('Filter', {
        user: setUser,
        type,
        criteria,
        results,
      });
    },
    [setUser]
  );

  const trackFinCredit = useCallback(
    (dateHour, section) => {
      analytics.track('Financing Tracker', {
        user: setUser,
        date_hour: dateHour,
        section,
      });
    },
    [setUser]
  );

  const trackQuickButtons = useCallback(
    props => {
      props.user = setUser;
      if (!props.section) props.section = formatSections;
      analytics.track('Quick Button', props);
    },
    [formatSections, setUser]
  );

  const trackAdditionalService = useCallback(
    (date_hour, inspection_id, type, year, make, model, winningBid, link_pdf, plateNumber) => {
      analytics.track('Additional Service', {
        user: setUser,
        date_hour,
        inspection_id,
        type,
        year,
        make,
        model,
        winningBid,
        link_pdf,
        plateNumber,
      });
    },
    [setUser]
  );

  // Novo evento: Usuário visualiza opções de serviços adicionais
  const trackServicesScreenViewed = useCallback(
    ({ inspectionId, auctionId, hasLaudoVenda, hasGarantia, hasCautelar, hasVisaoTotal }) => {
      const sessionId = sessionStorage.getItem('sessionId') || generateSessionId(); // ✅ CORRETO
      analytics.track('services_screen_viewed', {
        user: setUser,
        vehicle_id: inspectionId,
        auction_id: auctionId,
        session_id: sessionId,
        has_laudo_venda: hasLaudoVenda,
        has_garantia: hasGarantia,
        has_cautelar: hasCautelar,
        has_visao_total: hasVisaoTotal,
      });
    },
    [setUser]
  );

  const trackPreviewServiceClicked = useCallback(
    ({ inspectionId, auctionId, serviceType }) => {
      const sessionId = sessionStorage.getItem('sessionId') || generateSessionId();
      analytics.track('preview_service_clicked', {
        user: setUser,
        vehicle_id: inspectionId,
        auction_id: auctionId,
        session_id: sessionId,
        service_type: serviceType,
      });
    },
    [setUser]
  );

  const trackButtonServiceClicked = useCallback(
    ({ inspectionId, auctionId, totalServicesSelected, selectedServicesList }) => {
      const sessionId = sessionStorage.getItem('sessionId') || generateSessionId();
      analytics.track('button_service_clicked', {
        user: setUser,
        vehicle_id: inspectionId,
        auction_id: auctionId,
        session_id: sessionId,
        total_services_selected: totalServicesSelected,
        selected_services_list: selectedServicesList,
      });
    },
    [setUser]
  );

  const trackConfirmButtonServiceClicked = useCallback(
    ({
      inspectionId,
      auctionId,
      laudoVendaSelected,
      garantiaSelected,
      cautelarSelected,
      visaoTotalSelected,
      totalServicesConfirmed,
    }) => {
      const sessionId = sessionStorage.getItem('sessionId') || generateSessionId();
      analytics.track('confirm_button_service_clicked', {
        user: setUser,
        vehicle_id: inspectionId,
        auction_id: auctionId,
        session_id: sessionId,
        laudo_venda_selected: laudoVendaSelected,
        garantia_selected: garantiaSelected,
        cautelar_selected: cautelarSelected,
        visao_total_selected: visaoTotalSelected,
        total_services_confirmed: totalServicesConfirmed,
      });
    },
    [setUser]
  );

  const trackDealerPreferences = useCallback(
    ({
      dealershipId,
      dealershipName,
      email,
      phoneNumber,
      relatedNinja,
      actionType,
      estimatedNotifications,
      structuralCondition,
      auctionPreference,
      engineCondition,
      transmissionCondition,
      armoredPreference,
      kmPreference,
      kmMaxValue,
      agePreference,
      ageMinYear,
      channelWhatsapp,
      channelEmail,
      channelPushNotification,
    }) => {
      analytics.track('Dealer Preferences Saved', {
        // Identificação da loja
        dealership_id: dealershipId,
        dealership_name: dealershipName,
        dealership_email: email,
        dealership_phone: phoneNumber,
        related_ninja: relatedNinja || '',
        
        // Metadados do evento
        date_hour: new Date().toISOString(),
        action_type: actionType, // 'create' ou 'update'
        estimated_notifications: estimatedNotifications || 0,
        
        // Preferências (respostas das 7 perguntas)
        structural_condition: structuralCondition, // cautelar_aprovada, qualquer_cautelar, longarina_colunas
        auction_preference: auctionPreference, // sim_leilao, leilao_tipo_a, nao_leilao
        engine_condition: engineCondition, // sem_apontamentos_motor, com_apontamentos_motor
        transmission_condition: transmissionCondition, // sem_apontamentos_cambio, com_apontamentos_cambio
        armored_preference: armoredPreference, // blindados_comuns, somente_blindados, nao_blindados
        km_preference: kmPreference, // indiferente, ate_100k, ate_150k, ate_200k
        km_max_value: kmMaxValue || null, // valor numérico em km
        age_preference: agePreference, // indiferente ou especifica
        age_min_year: ageMinYear || null, // ano mínimo desejado
        
        // Canais de notificação
        channel_whatsapp: channelWhatsapp || false,
        channel_email: channelEmail || false,
        channel_push_notification: channelPushNotification || false,
      });
    },
    []
  );

  return {
    page,
    trackViewedDetails,
    trackButtonClicked,
    trackConfirmBid,
    trackCarouselInteraction,
    trackFilter,
    trackRemoveAutoBid,
    trackChangeMaximumAutoBid,
    trackTieAutoBid,
    trackReachedLimitAutoBid,
    trackQuickButtons,
    trackFinCredit,
    trackAdditionalService,
    trackServicesScreenViewed,
    trackPreviewServiceClicked,
    trackButtonServiceClicked,
    trackConfirmButtonServiceClicked,
    trackAuctionViewedDetails,
    trackAuctionRecommendedViewedDetails,
    trackDealerPreferences,
  };
};
